import React, { forwardRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from "clsx";
import { Button, MenuItem, TextField, withStyles } from "@material-ui/core";
import t from "../../../languages/pl";
import EmailTextField from './../../Common/InputFields/EmailTextField';
import { isRfid, notArrayOrEmpty, themeTypeToBool } from "../../../utils/helpers";
import PhoneTextField from './../../Common/InputFields/PhoneTextField';
import VpnKeyIcon from '@material-ui/icons/VpnKey';

const useStyles = theme => ({
    textField: {
        margin: theme.spacing(1),
        width: 250
    },
    rfidField: {
        margin: theme.spacing(1),
        width: 500 + theme.spacing(2)
    },
    helperText: {
        color: themeTypeToBool(theme.palette.type) ? "#000" : "inherit"
    },
    keyButton: {
        minWidth: 40,
        padding: theme.spacing(1)
    },
    keyTextfield: {
        margin: theme.spacing(1),
        width: 200,
    },
    row: {
        display: "flex",
        alignItems: "center"
    }
});

const initialState = {
    name: "",
    email: "",
    phone: "+48",
    driveType: "",
    driveTypeOutsideZone: "",
    rfidNo: "",
    sapId: "",
    groupId: "",
};

function validateRfid(text) {
    let pattern = /^[a-zA-Z0-9]{0,10}$/;
    return pattern.test(text);
}

const NewDriverForm = forwardRef((props, ref) => {
    const { classes, groups, data, onSubmit, isEdit = false, onMobileAppKeyGenerate, isPKP } = props;
    const [state, setState] = useState(initialState);
    const [rfidHelper, setRfidHelper] = useState("");

    useEffect(() => {
        if (data) {
            setState({
                name: data.kierowca_nazwa ? data.kierowca_nazwa : "",
                email: data.kierowca_email ? data.kierowca_email : "",
                phone: data.kierowca_telefon ? data.kierowca_telefon : "",
                driveType: data.kierowca_opis_rejon ? data.kierowca_opis_rejon : "",
                driveTypeOutsideZone: data.kierowca_opis_poza_rejon ? data.kierowca_opis_poza_rejon : "",
                rfidNo: data.kierowca_rfid ? data.kierowca_rfid : "",
                sapId: data.kierowca_sapid ? data.kierowca_sapid : "",
                groupId: data.kierowca_grupa.id ? data.kierowca_grupa.id : "",
            });
        }
    }, [data]);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (state.rfidNo.length !== 10)
            setRfidHelper(t.rfidHelperText);
        onSubmit && onSubmit(state);
    };

    const handleRfidChange = (e) => {
        if (validateRfid(e.target.value)) {
            setState({ ...state, rfidNo: e.target.value });
            rfidHelper && setRfidHelper(null);
        } else if (!isRfid(state.rfidNo))
            setRfidHelper(t.rfidHelperText);
    };

    const printGroupOptions = data => {
        if (notArrayOrEmpty(data))
            return;
        return data.map(row => <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>);
    };

    return (
        <form
            ref={ref}
            onSubmit={handleSubmit}
        >
            <div>
                <TextField
                    className={classes.textField}
                    label={t.firstAndLastName}
                    required
                    value={state.name}
                    onChange={(e) => setState({ ...state, name: e.target.value })}
                />
                <TextField
                    className={classes.textField}
                    label={t.group}
                    select
                    value={state.groupId}
                    onChange={(e) => setState({ ...state, groupId: e.target.value })}
                >
                    <MenuItem value="">
                        <em>{t.pick}</em>
                    </MenuItem>
                    {printGroupOptions(groups)}
                </TextField>
            </div>
            <div>
                <EmailTextField
                    className={classes.textField}
                    value={state.email}
                    onChange={(e) => setState({ ...state, email: e.target.value })}
                />
                <PhoneTextField
                    className={classes.textField}
                    label={t.phone}
                    value={state.phone}
                    onChange={(newValue) => setState({ ...state, phone: newValue })}
                />
            </div>
            <div>
                <TextField
                    className={classes.textField}
                    type="text"
                    label={t.driveType}
                    value={state.driveType}
                    onChange={(e) => setState({ ...state, driveType: e.target.value })}
                />
                <TextField
                    className={classes.textField}
                    type="text"
                    label={t.driveTypeOutsideZone}
                    value={state.driveTypeOutsideZone}
                    onChange={(e) => setState({ ...state, driveTypeOutsideZone: e.target.value })}
                />
            </div>
            <div className={classes.row}>
                <TextField
                    className={clsx({ [classes.rfidField]: !isEdit, [classes.textField]: isEdit })}
                    label={t.rfid}
                    required
                    value={state.rfidNo}
                    onChange={handleRfidChange}
                    helperText={rfidHelper}
                    FormHelperTextProps={{
                        classes: {
                            root: classes.helperText
                        }
                    }}
                />
                {
                    isEdit
                    &&
                    <>
                        <TextField
                            className={classes.keyTextfield}
                            label={t.mobileAppKey}
                            InputProps={{
                                readOnly: true
                            }}
                            value={data.kierowca_klucz_mobilny ? data.kierowca_klucz_mobilny : ""}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            classes={{ root: classes.keyButton }}
                            onClick={onMobileAppKeyGenerate}
                        >
                            <VpnKeyIcon/>
                        </Button>
                    </>
                }
            </div>
            {
                isPKP
                && <div className={classes.row}>
                    <TextField
                        className={clsx({ [classes.rfidField]: !isEdit, [classes.textField]: isEdit })}
                        label={t.sapid}
                        required
                        value={state.sapId}
                        onChange={(e) => setState({ ...state, sapId: e.target.value })}
                        FormHelperTextProps={{
                            classes: {
                                root: classes.helperText
                            }
                        }}
                    />
                </div>
            }
        </form>
    );
});


NewDriverForm.displayName = "NewDriverForm";

NewDriverForm.propTypes = {
    classes: PropTypes.object,
    groups: PropTypes.array,
    isEdit: PropTypes.bool,
    isPKP: PropTypes.bool,
    data: PropTypes.object,
    onSubmit: PropTypes.func,
    onMobileAppKeyGenerate: PropTypes.func
};

export default withStyles(useStyles)(NewDriverForm);
